import { createRef } from 'react';
import { observable, action, computed, flow, makeObservable } from 'decorators';
import { toJS } from 'mobx';
import moment from 'moment';
import api from 'services/api';
import log from 'services/log';
import auth from 'services/auth';
import Form, { isRequired, isEmail, customError, checkIf } from 'components/form';
import NavLink from 'components/navLink';
import { ValidationError } from 'errors';
import { singleLineAddress } from 'utils/address';
import { longDate, shortTime } from 'utils/date';
import { toMoney } from 'utils/number';
import { enumToList } from 'utils/enums';
import { toQueryString } from 'utils/query';
import { genderIdentityOptions, pronounsOptions } from 'services/data/clients';
import { getCachedTimezone } from 'services/data/timezones';
import userService from 'services/data/user';
import themeService from 'theme';

import TimePickStepStore from './timePickStep.store';

import { combineForms } from 'services/forms';
import { addressForm } from 'services/forms/address';
import { insurerForm } from 'services/forms/insurer';

function redirectToPageGlobal(url) {
  try {
    window.top.location.href = url;
  } catch {
    window.location.href = url;
  }
}

export default class SchedulerStore {
  @observable isLoading = true;
  @observable currentStep = 0;
  @observable hadError = false;
  @observable errorMessage = null;
  @observable hasSteped = false;

  @observable settings = null;
  @observable _types = [];
  @observable _typeFilter = [];
  @observable _locations = [];
  @observable _locationFilter = [];
  @observable _clinicians = [];
  @observable _cliniciansFilter = [];
  @observable mappings = {};

  @observable selectedType = null;
  @observable blockTypeSelection = false;
  @observable _blockQuestionSelection = false;
  @observable selectedLocation = null;
  @observable blockLocSelection = false;
  @observable selectedClinician = null;
  @observable blockClinicianSelection = false;
  @observable selectedTimezone = null;
  @observable selectedTime = null;
  @observable selectedGroupId = null;

  @observable questionsForms = null;

  @observable timePick = null;
  @observable detailsForm = null;

  @observable isShowingAddress = false;
  @observable showingAddressIndex = -1;
  @observable address = null;

  @observable insurers = [];
  @observable copay = null;
  @observable editingInsurer = false;

  @observable isBooking = false;
  @observable isComplete = false;
  @observable bookingData = null;
  @observable _retrySessionId = null;
  @observable isCanceling = false;

  constructor({ onSettings, appType, appLoc, appPrac, gType, gLoc, gPrac, q, groupId, bId, bs, opts, internalMode, onConfirmReady } = {}) {
    makeObservable(this);
    this.internalMode = internalMode;
    this._opts = opts;
    this._onSettings = onSettings;
    this._recaptchaRef = createRef();
    this.onConfirmReady = onConfirmReady;
    this.init({ appType, appLoc, appPrac, gType, gLoc, gPrac, q, groupId, bId, bs }).done();
  }

  @computed get isValid() {
    if (this.isLoading || this.isRetry) { return true; }
    if (!this.settings) { return false; }
    if (!this.types.length || !this.locations.length) { return false; }
    if (!this.hideClinicians && !this.clinicians.length) { return false; }
    return true;
  }

  @computed get shouldStopNav() {
    return this.isBooking || this.isComplete;
  }

  @computed get types() {
    if (!this._typeFilter.length) { return this._types; }
    return this._types.filter(t => this._typeFilter.includes(t.id));
  }

  @computed get locations() {
    if (!this._locationFilter.length) { return this._locations; }
    return this._locations.filter(t => this._locationFilter.includes(t.id));
  }

  @computed get clinicians() {
    if (!this._cliniciansFilter.length) { return this._clinicians; }
    return this._clinicians.filter(t => this._cliniciansFilter.includes(t.id));
  }

  @computed get selectedTypeName() {
    return this.selectedType?.name;
  }

  @computed get selectedLocationName() {
    const loc = this.selectedLocation;
    if (!loc || this.isShowingAddress) { return null; }
    return loc.name;
  }

  @computed get selectedLocationAddress() {
    const loc = this.selectedLocation;
    if (!loc || this.isShowingAddress) { return null; }
    return loc.isHomeVisit ? singleLineAddress(this.address.values().address) : loc.addressStr;
  }

  @computed get selectedClinicianName() {
    return this.selectedClinician?.name;
  }

  @computed get selectedTimeName() {
    if (!this.selectedTime) { return null; }

    const tz = getCachedTimezone(this.selectedTimezone)?.info?.abbr;
    const time = this.selectedTime ? moment.tz(this.selectedTime, this.selectedTimezone) : null;
    return `${longDate(time)} ${shortTime(time)} ${tz}`;
  }

  @computed get availableLocations() {
    if (!this.selectedType) { return []; }

    const locIds = Object.keys(this.mappings[this.selectedType.id]);
    return this.locations.filter(l => locIds.includes(l.id));
  }

  @computed get availableClinicians() {
    if (!this.selectedType || !this.selectedLocation) { return []; }

    const clinIds = this.mappings[this.selectedType.id][this.selectedLocation.id];
    const available = this.clinicians.filter(c => clinIds.includes(c.id));

    if (!this.settings.hideAnyClinicians && available.length > 1) {
      available.unshift({
        id: null,
        name: 'Any Practitioner',
        description: "I don't mind who provides this service."
      });
    }

    return available;
  }

  @computed get hideClinicians() {
    return !this.internalMode && !!this.settings?.hideClinicians;
  }

  @computed get hideQuestions() {
    const q = this.settings?.questions;
    return this._blockQuestionSelection || q == null || q.length === 0;
  }

  @computed get needsClientInfo() {
    return !this._opts?.clientId;
  }

  @computed get hasClientSection() {
    return this.needsClientInfo;
  }

  @computed get hasInsuranceInfo() {
    if (!this.needsClientInfo) { return false; }
    const type = this.selectedType;
    return !!(auth.isUsBilling && type?.needsInsurance);
  }

  @computed get hideCopay() {
    return !!(this.selectedType?.hideCopay);
  }

  @computed get insuranceRequired() {
    return this.hasInsuranceInfo && !!(this.selectedType?.insuranceRequired);
  }

  @computed get hasPhotoId() {
    if (!this.needsClientInfo) { return false; }
    const type = this.selectedType;
    return !!(auth.isUsBilling && type?.needsPhotoId);
  }

  @computed get needsAddressInfo() {
    if (!this.needsClientInfo) { return false; }
    const { hasInsuranceInfo, insurers, settings, selectedLocation } = this;
    return !!(!settings?.hideAddress || (hasInsuranceInfo && insurers.length) || selectedLocation?.isHomeVisit);
  }

  @computed get showAddressInfo() {
    if (!this.needsClientInfo) { return false; }
    const { needsAddressInfo, selectedLocation } = this;
    return needsAddressInfo && !selectedLocation?.isHomeVisit;
  }

  @computed get paymentsEnabled() {
    return !this.internalMode
      && !!(this.settings?.paymentProviderType)
      && (this._opts?.payments == null ? !this._opts?.clientId : this._opts.payments);
  }

  @computed get needsPaymentInfo() {
    if (!this.paymentsEnabled) { return false; }
    const type = this.selectedType;
    return !!(type && (type.upFrontPrice || type.needsCreditCard));
  }

  @computed get hasUpfrontPayment() {
    if (!this.paymentsEnabled) { return false; }
    return this.selectedType?.upFrontPrice > 0;
  }

  @computed get bookButtonText() {
    if (this.needsPaymentInfo && this.selectedType.upFrontPrice > 0) {
      return `Pay ${toMoney(this.selectedType.upFrontPrice)} and book appointment`;
    }

    return 'Book Appointment';
  }

  @computed get questionsValid() {
    if (this.questionsForms == null) { return true; }

    const allResponses = this.questionsForms.map(q => q.values());

    // Make sure that every question has been answered first
    if (allResponses.find(q => q.required && (Array.isArray(q.answer) ? !q.answer.length : !q.answer))) { return true; }

    const appTypeGroupIds = allResponses.filter(q => q.type === 'app_type').map(q => q.answer).flat().filter(a => a);
    const validAppTypeIds = appTypeGroupIds.length ? this._types.filter(t => appTypeGroupIds.every(g => t.groups.includes(g))).map(t => t.id) : null;
    if (appTypeGroupIds.length && !validAppTypeIds.length) { return false; }

    const locGroupIds = allResponses.filter(q => q.type === 'location').map(q => q.answer).flat().filter(a => a);
    const validLocIds = locGroupIds.length ? this._locations.filter(l => locGroupIds.every(g => l.groups.includes(g))).map(l => l.id) : null;
    if (locGroupIds.length && !validLocIds.length) { return false; }

    const clinGroupIds = allResponses.filter(q => q.type === 'clinician').map(q => q.answer).flat().filter(a => a);
    const validClinIds = clinGroupIds.length ? this._clinicians.filter(c => clinGroupIds.every(g => c.groups.includes(g))).map(c => c.id) : null;
    if (clinGroupIds.length && !validClinIds.length) { return false; }

    // If we have a combination, we need to check if our mappings have at least one good combination
    if (((validAppTypeIds ? 1 : 0) + (validLocIds ? 1 : 0) + (validClinIds ? 1 : 0)) > 1) {
      for (const typeId of Object.keys(this.mappings)) {
        const locs = this.mappings[typeId];
        for (const locId of Object.keys(locs)) {
          // We have a valid combination!
          if (
            (validAppTypeIds == null || validAppTypeIds.includes(Number(typeId)))
            && (validLocIds == null || validLocIds.includes(locId))
            && locs[locId].find(cl => validClinIds == null || validClinIds.includes(cl))
          ) {
            return true;
          }
        }
      }
      // No valid combination...
      return false;
    }

    return true;
  }

  @computed get isRetry() {
    return !!this._retrySessionId;
  }

  setScrollElement(el) {
    this._scroll = el;
  }

  @action goBackToStep(step) {
    if (this.shouldStopNav && step < 6) { return; }

    switch (step) {
      case 0:
      case 1:
        this.selectedType = null;
        // falls through
      case 2:
        this.selectedLocation = null;
        this.isShowingAddress = false;
        // falls through
      case 3:
        this.selectedClinician = null;
        // falls through
      case 4:
        this.selectedTime = null;
        this.selectedTimezone = null;
        this.selectedGroupId = null;
        if (this.timePick) {
          this.timePick.selectedDay = null;
        }
        if (this.onConfirmReady) {
          this.onConfirmReady(null);
        }
        // falls through
      case 5:
      case 6:
      case 7:
      case 8:
        // Nothing to do in this situation
    }

    // The current step is from the actual steps available
    if (!this.hasUpfrontPayment && step > 6) { step--; }
    if (!this.needsPaymentInfo && step > 5) { step--; }
    if (!this.hasClientSection && step > 4) { step--; }
    if (this.hideClinicians && step > 2) { step--; }
    if (this.hideQuestions && step > 0) { step--; }
    this.currentStep = step;
  }

  @flow *saveQuestions(e) {
    yield Promise.all(this.questionsForms.map(q => q.submitPromise(e)));

    const allResponses = this.questionsForms.map(q => q.values());

    const appTypeGroupIds = allResponses.filter(q => q.type === 'app_type').map(q => q.answer).flat().filter(a => a);
    const validAppTypeIds = appTypeGroupIds.length ? this._types.filter(t => appTypeGroupIds.every(g => t.groups.includes(g))).map(t => t.id) : null;

    const locGroupIds = allResponses.filter(q => q.type === 'location').map(q => q.answer).flat().filter(a => a);
    const validLocIds = locGroupIds.length ? this._locations.filter(l => locGroupIds.every(g => l.groups.includes(g))).map(l => l.id) : null;

    const clinGroupIds = allResponses.filter(q => q.type === 'clinician').map(q => q.answer).flat().filter(a => a);
    const validClinIds = clinGroupIds.length ? this._clinicians.filter(c => clinGroupIds.every(g => c.groups.includes(g))).map(c => c.id) : null;

    if (appTypeGroupIds || validLocIds || validClinIds) {
      // We need to work out which types are actually viable now
      const typeFilter = [];
      for (const typeId of Object.keys(this.mappings)) {
        const locs = this.mappings[typeId];
        for (const locId of Object.keys(locs)) {
          // We have a valid combination!
          if (
            (validAppTypeIds == null || validAppTypeIds.includes(Number(typeId)))
            && (validLocIds == null || validLocIds.includes(locId))
            && locs[locId].find(cl => validClinIds == null || validClinIds.includes(cl))
          ) {
            typeFilter.push(Number(typeId));
            break;
          }
        }
      }

      this._typeFilter = typeFilter;
      this._locationFilter = validLocIds || [];
      this._cliniciansFilter = validClinIds || [];
    }

    this._nextStep();
  }

  @action selectType(type) {
    this.selectedType = type;
    this._nextStep();
  }

  @action selectLocation(loc, index) {
    this.selectedLocation = loc;

    if (loc.isHomeVisit) {
      this.isShowingAddress = true;
      this.showingAddressIndex = index;
    } else {
      this._nextStep();
    }
  }

  @action selectTime(time, timezone, groupId) {
    this.selectedTime = time;
    this.selectedTimezone = timezone;
    this.selectedGroupId = groupId;
    if (this.onConfirmReady) {
      this.onConfirmReady(this._getBookingData());
    }
    this._nextStep();
  }

  @action cancelAddress() {
    this.selectedLocation = null;
    this.isShowingAddress = false;
  }

  selectAddress(e) {
    this.address.submitPromise(e)
      .then(action(() => {
        this._nextStep();
        this.isShowingAddress = false;
      }))
      .catch(ValidationError, () => {})
      .catch(log.catchAndNotify)
      .done();
  }

  @action selectClinician(cl) {
    this.selectedClinician = cl;
    this._nextStep();
  }

  @action onEditingInsurer(e) {
    this.editingInsurer = e;
  }

  @flow *submitDetails(e) {
    e.persist();
    yield this._validateDetails(e);

    this.isBooking = true;
    this.scrollToView();
    try {
      if (this._retrySessionId) {
        redirectToPageGlobal(`${window.location.origin}/checkout?checkoutId=${this._retrySessionId}`);
      } else {
        const data = this._getBookingData();
        data.session = auth.getAccessUrlHash();
        data.filters = this._lastResetSet;
        const recaptHeader = yield (this._recaptchaRef.current ? this._recaptchaRef.current.execute() : Promise.resolve());
        const res = yield api.post('appointment/scheduling/online/book', data, { headers: { 'X-Captcha-Resp': recaptHeader } });
        const { sessionId, details } = res.data;
        if (sessionId) {
          redirectToPageGlobal(`${window.location.origin}/checkout?checkoutId=${sessionId}`);
        } else {
          this._completeBooking(details);
          this.isBooking = false;
        }
      }
    } catch (e) {
      this.isBooking = false;
      throw e;
    }
  }

  @flow *cancelBooking() {
    this.isCanceling = true;
    try {
      yield api.post(`appointment/scheduling/online/book/session/${this._cancelId}/cancel?sign=${encodeURIComponent(this._cancelSign)}`);
    } catch {}

    this.isComplete = false;
    this._retrySessionId = null;
    this._cancelId = null;
    this._cancelSign = null;
    this.hadError = false;
    this.errorMessage = null;
    this._restart(this._lastResetSet);
    this.goBackToStep(0);
    this.isCanceling = false;
  }

  complete() {
    if (this.settings.redirectUrl) {
      const d = this.bookingData;
      const queryParams = {
        appId: d.id,
        appType: d.typeId,
        appLoc: d.locId,
        appPrac: d.clId,
        appTime: d.time,
        appTz: d.tz
      };
      const url = `${this.settings.redirectUrl}?${toQueryString(queryParams)}`;
      redirectToPageGlobal(url);
    } else {
      this.goBackToStep(8);
    }
  }

  getTheme(override) {
    const theme = override || this.settings?.theme || {};
    return themeService.createTheme({
      primary: theme.mainColor,
      background: theme.backgroundColor,
      paper: theme.paperColor
    });
  }

  scrollToView() {
    if (this._scroll) {
      setTimeout(() => this._scroll?.scrollIntoView(true), 1);
    }
  }

  dispose() {
    this._scroll = null;
    this._isDisposed = true;
    if (this.timePick) {
      this.timePick.dispose();
      this.timePick = null;
    }
  }

  @action _nextStep() {
    this.currentStep++;
    this.hasSteped = true;
    this.scrollToView();
  }

  async _validateDetails(e) {
    if (this._retrySessionId) { return; }

    const addressCheck = this.needsAddressInfo ? this.address.submitPromise(e) : Promise.resolve();
    const insurerCheck = this.hasInsuranceInfo && this.editingInsurer ? Promise.reject(new ValidationError('You are still editing an insurance, please save it first')) : Promise.resolve();
    const detailsFormCheck = this.needsClientInfo ? this.detailsForm.submitPromise(e) : Promise.resolve();

    return await Promise.all([ detailsFormCheck, addressCheck, insurerCheck ]);
  }

  @action _completeBooking(details, paymentSessionId) {
    const { id, appType, location, clinician, time, timezone, invoiceId, invoiceLink } = details;

    this.selectedType = appType;
    this.selectedLocation = location;
    this.selectedTime = time;
    this.selectedTimezone = timezone;
    if (clinician) { this.selectedClinician = clinician; }

    this.isComplete = true;
    if (paymentSessionId) {
      this._retrySessionId = paymentSessionId;
      this.goBackToStep(6);
    } else {
      this._retrySessionId = null;
      this.bookingData = {
        id,
        typeId: appType.id,
        locId: location.id,
        clId: clinician?.id,
        time,
        tz: timezone,
        invId: invoiceId,
        invLink: invoiceLink
      };

      if (this.hasUpfrontPayment) {
        this.goBackToStep(7);
      } else {
        this.complete();
      }
    }
  }

  _getBookingData() {
    const client = this.needsClientInfo ? this.detailsForm.values() : {};
    if (this.needsClientInfo) {
      client.timezone = this.selectedTimezone;
      if (this.needsAddressInfo) {
        client.address = this.address.values().address;
      }
      if (this.hasInsuranceInfo) {
        client.insurers = toJS(this.insurers);
        client.copay = this.copay;
      } else {
        client.insurers = [];
        client.copay = null;
      }
    }

    const clinicianId = this.selectedClinician?.id;
    return {
      appointmentTypeId: this.selectedType.id,
      locationId: this.selectedLocation.id,
      clinicianId,
      clinicianFilter: !clinicianId && this._cliniciansFilter?.length ? this._cliniciansFilter : null,
      time: this.selectedTime,
      groupId: this.selectedGroupId,
      emailOptIn: client.emailOptIn,
      smsOptIn: client.smsOptIn,
      client,
      options: this._opts
    };
  }

  @action _restart({ appType, appLoc, appPrac, gType, gLoc, gPrac, q, groupId }) {
    let step = 0;
    this.hasSteped = false;
    this._blockQuestionSelection = false;

    this.selectedType = null;
    this.blockTypeSelection = false;

    this.selectedLocation = null;
    this.blockLocSelection = false;

    this.selectedClinician = null;
    this.blockClinicianSelection = false;

    if (!groupId) {
      this.selectedTime = null;
      this.selectedTimezone = null;
    }

    const hasTypes = !!(appType || []).length || !!(gType || []).length;
    const hasLocs = !!(appLoc || []).length || !!(gLoc || []).length;
    const hasClins = !!(appPrac || []).length || !!(gPrac || []).length;

    this._lastResetSet = { appType, appLoc, appPrac, gType, gLoc, gPrac, q, groupId };
    if (hasTypes || hasLocs || hasClins || groupId) {
      this._blockQuestionSelection = !!groupId || !q; // Questions optional with advanced options

      // If we don't have questions, we can try to pre-select a number of things
      if (this.hideQuestions && this.types.length === 1) {
        this.selectedType = this.types[0];
        step += 2;
        this.blockTypeSelection = true;

        if ((hasLocs || hasClins || groupId) && this.availableLocations.length === 1) {
          this.selectedLocation = this.availableLocations[0];
          step++;
          this.blockLocSelection = true;

          // We unshift 'All' to our available clinicians if we have one
          // So that is why we take second item
          if (!this.hideClinicians && (hasClins || groupId) && this.availableClinicians.length === 1) {
            this.selectedClinician = this.availableClinicians[0];
            step++;
            this.blockClinicianSelection = true;
          }

          if ((this.hideClinicians || this.blockClinicianSelection) && groupId && this.selectedTime) {
            step++;
          }

          if (this.timePick && (this.hideClinicians || this.blockClinicianSelection) && !groupId) {
            this.timePick.noFocusFirstLoad = true;
          }
        }
      }
    }
    this.goBackToStep(step);
  }

  @flow *_restoreFromSession({ sessionId: sId, sign }, hideLoading) {
    if (!hideLoading) { this.isLoading = true; }
    this.hadError = false;
    this.errorMessage = null;

    try {
      const res = yield api.get(`appointment/scheduling/online/book/session/${sId}?sign=${encodeURIComponent(sign)}`);
      const { details, sessionId } = res.data;
      this._cancelId = sId;
      this._cancelSign = sign;
      yield this._completeBooking(details, sessionId);
    } catch (e) {
      this._retrySessionId = 'fail';
      this.hadError = true;
      this.errorMessage = 'Booking session has expired';
      return null;
    }

    this.isLoading = false;
  }

  @flow *init({ bId, bs, ...filters }) {
    this.isLoading = true;
    this.hadError = false;
    this.errorMessage = null;

    try {
      const res = yield Promise.all([
        api.post('appointment/scheduling/online/settings', { options: this._opts, filters }),
        userService.clearinghouse.waitForValue() // For insurer form
      ]);

      if (this._isDisposed) { return; }

      const mainResult = res[0].data;

      if (mainResult != null) {
        const { settings, availability } = mainResult;
        const { types, locations, clinicians, mappings, time, timezone } = availability;

        this.settings = settings;
        this._types = types;
        this._locations = locations;
        this._clinicians = clinicians;
        this.mappings = mappings;
        this.selectedTime = time;
        this.selectedTimezone = time ? (moment.tz.guess() || timezone) : null;

        this.timePick = new TimePickStepStore(this);
        this.questionsForms = this._createQuestionForms();
        this.detailsForm = new Form(this._createDetailsFields());
        this.address = new Form(this._createAddressFields());

        // If we have some query params, use them to init the form...
        this._restart(filters);
        this._onSettings && this._onSettings(settings);
      }

      if (bId) {
        this._blockQuestionSelection = true;
        yield this._restoreFromSession({ sessionId: bId, sign: bs });
      }

      this.isLoading = false;
    } catch (err) {
      // Ignore Unauthorized exceptions and bad tokens
      const status = err.response?.status;
      if (status !== 401 && status !== 400) {
        log.error('Error getting init scheduler info', err);
      }
      this.hadError = true;
    }
  }

  @action _createQuestionForms() {
    const questions = this.settings.questions;
    if (questions == null) { return null; }

    return questions.map(q => {
      const groups = q.groups;
      if (!q.isRequired && !q.allowMultiple) { groups.unshift({ id: null, name: null }); }

      return new Form({
        fields: [
          {
            name: 'question',
            value: q.question
          },
          {
            name: 'type',
            value: q.type
          },
          {
            name: 'required',
            value: q.isRequired
          },
          {
            name: 'answer',
            validators: q.isRequired ? [ isRequired ] : undefined,
            type: 'select',
            placeholder: q.isRequired ? undefined : 'Optional',
            extra: {
              multiple: q.allowMultiple,
              valueKey: 'id',
              options: groups
            }
          }
        ]
      });
    });
  }

  @action _createAddressFields() {
    const validators = {
      'address.street1': [ isRequired ],
      'address.suburb': [ isRequired ],
      'address.postcode': [ isRequired ],
      'address.state': [ isRequired ]
    };
    return combineForms(addressForm('address'), { validators });
  }

  _createDetailsFields() {
    const settings = this.settings;
    const fields = [
      'givenName', 'lastName', 'preferredName', 'email', 'emailOptIn', 'smsOptIn', 'agreeOptIn', 'phone', 'gender', 'genderIdentity', 'pronouns', 'dob', 'copay', 'notes', 'sign', 'signName',
      'insurerPhotoId', 'insurerPhotoIdTempAccessKey'
    ];

    const labels = {
      givenName: 'Given Name(s)',
      lastName: 'Last Name',
      preferredName: 'Preferred Name (Optional)',
      email: 'Email',
      phone: auth.lang.toTitleCase('cellphone'),
      gender: 'Sex',
      genderIdentity: 'Gender Identity',
      pronouns: 'Pronouns',
      dob: 'Date of Birth',
      copay: 'Copayment',
      signName: 'Signed By'
    };

    const types = {
      email: 'email',
      phone: 'phone',
      gender: 'select',
      dob: 'date',
      genderIdentity: 'selectasync',
      pronouns: 'selectasync',
      copay: 'money',
      notes: 'textarea',
      sign: 'signature',
      emailOptIn: 'checkbox',
      smsOptIn: 'checkbox',
      agreeOptIn: 'checkbox'
    };

    const validators = {
      givenName: [ isRequired ],
      lastName: [ isRequired ],
      email: [ isEmail, isRequired ],
      phone: settings.hidePhone ? undefined : [ isRequired ],
      gender: settings.hideGender ? undefined : [ isRequired ],
      dob: settings.hideDOB ? undefined : [ isRequired ],
      sign: !settings.legal ? undefined : [ customError(isRequired, 'Signature required to schedule this service') ],
      signName: !settings.legal ? undefined : [ isRequired ],
      insurerPhotoId: [ checkIf(() => this.hasPhotoId, [ isRequired ]) ],
      insurers: [ ({ field }) => {
        if (this.editingInsurer) { return [ false, 'You are still editing an insurance, please save it first' ]; }
        if (this.insuranceRequired && field.value.length === 0) { return [ false, 'At least one Insurer is required' ]; }
        return [ true, null ];
      } ],
      agreeOptIn: [ checkIf(({ form }) => form.$('emailOptIn').value || form.$('smsOptIn').value, [ m => [ isRequired(m)[0], 'When opting in to email or text messages, you must agree to Kalix\'s Client Privacy and Security Policy.' ] ]) ]
    };

    const placeholders = {
      givenName: 'First Middle',
      genderIdentity: 'Type in other option',
      pronouns: 'Type in other option'
    };

    const extra = {
      givenName: {
        autoComplete: 'given-name'
      },
      lastName: {
        autoComplete: 'family-name'
      },
      email: {
        autoComplete: 'email'
      },
      phone: {
        autoComplete: 'tel'
      },
      gender: {
        autoComplete: 'sex',
        options: enumToList(auth.enums.gender, { zeroAsEmpty: true })
      },
      dob: {
        forceInline: true,
        autoComplete: 'bday',
        maxDate: moment()
      },
      genderIdentity: {
        allowFreeInput: true,
        variableHeight: true,
        loadOptions: q => ({ options: genderIdentityOptions })
      },
      pronouns: {
        allowFreeInput: true,
        loadOptions: q => ({ options: pronounsOptions })
      },
      copay: {
        info: 'Enter the fixed amount set by the insurance plan (co-payment) you are expected to pay for this service.'
      },
      emailOptIn: {
        calcFields: () => ({
          text: `I wish to receive email messages, including appointment reminders from ${auth.user.orgPublicName}`
        })
      },
      smsOptIn: {
        calcFields: () => ({
          text: `I wish to receive text messages (SMS), including appointment reminders from ${auth.user.orgPublicName}`
        })
      },
      agreeOptIn: {
        text: <span>I agree to Kalix&apos;s <NavLink helpLink="articles/5034464-client-privacy-policy">Client Privacy & Security Policy</NavLink></span>
      },
      sign: {
        inline: true
      },
      signName: {
        autoComplete: 'name'
      },
      'insurers[].name': {
        shrink: true
      },
      'insurers[].insuredNumber': {
        shrink: true
      },
      'insurers[].medicareTypeCode': {
        shrink: true
      },
      'insurers[].groupId': {
        shrink: true
      },
      'insurers[].insuredName.givenName': {
        shrink: true
      },
      'insurers[].insuredName.lastName': {
        shrink: true
      },
      'insurers[].insuredDOB': {
        shrink: true
      },
      'insurers[].insuredGender': {
        shrink: true
      }
    };

    return combineForms(insurerForm('insurers[]', { simplified: true, isTentative: true }), { fields, labels, types, validators, placeholders, extra });
  }
}